import React, { useEffect, useCallback, useState } from 'react';
import { withRouter } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import uniqueBy from 'lodash/uniqBy';
import { Checkbox, Button, message } from 'antd';
import GaragePageHeader from '../components/GaragePageHeader';
import { getGarageById } from '../GarageEditPage/actions';
import { updateSpecialityAssociations, listSpecialities, getSpecialitiesByOwner } from './actions';
import Menu from '../components/Menu';
import Styled from './styled';

const CheckboxGroup = Checkbox.Group;

function checksFirst(a) {
  if (a.value) {
    if (a.value.includes('check')) return -1;
  } else if (a.includes('check')) return -1;
  return 0;
}

function isSpecialityEditDisabled(value) {
  // booking fee should always be active
  return value === 'booking-admin-fee';
}

function getCheckboxGroupOptions(specialities) {
  return specialities
    .map((s) => ({
      label: s.external_id,
      value: s.external_id,
      disabled: isSpecialityEditDisabled(s.external_id),
    }))
    .sort((a, b) => (a.value > b.value ? 0 : -1))
    .sort(checksFirst);
}

function getActiveSpecialities(associations) {
  const activeSpecialitites = associations.map((c) => c.speciality);
  activeSpecialitites.sort().sort(checksFirst);
  return activeSpecialitites;
}

function removeDuplicateSpecialities(specialities) {
  return [...new Set(specialities)];
}

function removeDuplications(specialities) {
  return uniqueBy(specialities, 'external_id');
}

function getSpecialityAssociation(specialities, garageId, specialityExternalId) {
  const speciality = specialities.find((s) => s.external_id === specialityExternalId);
  if (speciality) {
    return {
      specialityOwnerId: garageId,
      specialityId: speciality.id,
      speciality: specialityExternalId,
      associationId: undefined,
    };
  }
  return null;
}

function prepareAssociations(associations, specialities, garageId) {
  const associationSpecialities = get(associations[0], 'specialities', []);
  const uniqueAssociations = removeDuplicateSpecialities(associationSpecialities.map((c) => c.speciality));
  const preparedAssociations = uniqueAssociations.map((uniqueAssociation) => {
    const { associationId, speciality, specialityId } = associationSpecialities.find(
      (d) => uniqueAssociation === d.speciality
    );
    return {
      associationId,
      speciality,
      specialityId,
    };
  });

  // booking-admin-fee should be assigned by default
  if (!associationSpecialities.find((initial) => initial.speciality === 'booking-admin-fee')) {
    preparedAssociations.push(getSpecialityAssociation(specialities, garageId, 'booking-admin-fee'));
  }

  return preparedAssociations;
}

function GarageSpecialitiesPage({ history, match }) {
  const garageId = match.params.garageId;
  const [showErrors, setShowErrors] = useState(false);

  const [state, setState] = useState({
    garage: null,
    specialities: [],
    initialAssociations: [],
    associations: [],
  });

  const getData = () =>
    Promise.all([getGarageById(garageId), listSpecialities(), getSpecialitiesByOwner(garageId)])
      .then(([newGarage, newSpecialities, newSpecialityAssociations]) => {
        setState((prevState) => ({
          ...prevState,
          garage: newGarage,
          specialities: removeDuplications(newSpecialities),
          initialAssociations: get(newSpecialityAssociations[0], 'specialities', []),
          associations: prepareAssociations(newSpecialityAssociations, newSpecialities, garageId),
        }));
      })
      .catch((err) => {
        message.error(err);
        console.log('Error on Garages page Specialities:', err);
      });

  useEffect(() => {
    getData();
  }, [garageId]);

  const onChange = (newChecklist) => {
    let updatedChecklist = [...state.associations];

    if (newChecklist.length > state.associations.length) {
      const changedAssociation = newChecklist.find(
        (newItem) => !state.associations.find((oldItem) => oldItem.speciality === newItem)
      );

      const associationSpeciality = state.specialities.find(
        (speciality) => speciality.external_id === changedAssociation
      );

      const newAssociation = {
        specialityOwnerId: garageId,
        specialityId: associationSpeciality.id,
        speciality: changedAssociation,
      };
      updatedChecklist = [...updatedChecklist, newAssociation];
    }

    if (newChecklist.length < state.associations.length) {
      const removedSpeciality = state.associations.filter(
        (oldItem) => !newChecklist.includes(oldItem.speciality)
      );

      updatedChecklist = updatedChecklist.filter(
        (item) => item.speciality !== removedSpeciality[0].speciality
      );
    }

    setState((prevState) => ({
      ...prevState,
      associations: updatedChecklist,
    }));
  };

  const onCheckAllChange = (e) => {
    if (e.target.checked) {
      setState((prevState) => ({
        ...prevState,
        associations: state.specialities.map((speciality) => {
          const hasAssociation = state.associations.find(
            (association) => association.specialityId === speciality.id
          );
          if (hasAssociation) return hasAssociation;

          return {
            specialityOwnerId: garageId,
            specialityId: speciality.id,
            speciality: speciality.external_id,
          };
        }),
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        associations: [getSpecialityAssociation(state.specialities, garageId, 'booking-admin-fee')],
      }));
    }
  };

  const saveSpecialities = () => {
    const { associations, initialAssociations } = state;

    const removedAssociation = initialAssociations
      .filter((initial) => !associations.find((current) => initial.speciality === current.speciality))
      .map((association) => ({
        associationId: association.associationId,
        specialityId: association.specialityId,
        specialityOwnerId: association.specialityOwnerId,
      }));

    const newAssociations = associations
      .filter((current) => !initialAssociations.find((initial) => initial.speciality === current.speciality))
      .map((association) => ({
        specialityId: association.specialityId,
        specialityOwnerId: association.specialityOwnerId,
      }));

    if (isEmpty(associations)) {
      return setShowErrors(true);
    }
    setShowErrors(false);

    return updateSpecialityAssociations({
      remove: removedAssociation,
      add: newAssociations,
    }).then(() => message.info('Specialities have been updated'));
  };

  if (!state.garage) return null;

  return (
    <Styled>
      <GaragePageHeader history={history} name={state.garage?.businessName} />
      <Menu selectedItem="specialities" garageId={garageId} history={history} />

      {showErrors && (
        <div className="errors">
          <div>Errors</div>
          <div>You need to check at least one speciality before saving this garage</div>
        </div>
      )}

      <div className="specialities">
        <Checkbox
          onChange={onCheckAllChange}
          checked={state.associations.length === state.specialities.length}
        >
          Check all specialities
        </Checkbox>

        <div className="line" />

        <CheckboxGroup
          options={getCheckboxGroupOptions(state.specialities)}
          value={getActiveSpecialities(state.associations)}
          onChange={onChange}
        />

        <Button onClick={saveSpecialities}>Save</Button>
      </div>
    </Styled>
  );
}

export default withRouter(GarageSpecialitiesPage);
